/* eslint-disable @typescript-eslint/no-floating-promises */
import React from 'react';
import { useNavigate } from 'react-router';
import { useTranslate } from '../../mixins/translate/translate';
import { openApiFetchDelete, openApiFetchGet, openApiFetchPost } from '../../store/openApiFetch';
import { SortDirections } from '../../utils/sorting';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import { Modal } from '../common/Modal/Modal';
import {
    Content,
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelFilterSelect,
    EditPanelSection,
    ISelectOption,
    Panel,
    PanelBody,
    PanelButtonLink,
    Table,
    TableBody,
    TableButtons,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    Tray,
} from '../elements';
import { Spinner } from '../spinners';
import { EquipmentExpirationLabel } from '../utils/equipmentExpirationLabel';
import { useReactAsync } from '../utils/useReactAsync';
import { TopBar } from './Common/TopBar';

async function loadEquipmentPayload({ equipmentId }: { equipmentId: string }) {
    const result = await openApiFetchGet('/api/equipmentPayload', { equipmentId });
    return result;
}

export function EquipmentPayload(props: { params: { id?: string } }) {
    const { id } = props.params;
    if (!id) {
        throw new Error('Equipment id is not provided');
    }
    const translate = useTranslate();
    const navigate = useNavigate();
    const { data, reload, isLoading } = useReactAsync(loadEquipmentPayload, { equipmentId: id }, []);
    const [isEquipmentSelectVisible, setIsEquipmentSelectVisible] = React.useState(false);

    async function removePayload(equipmentPartId: string) {
        await openApiFetchDelete('/api/equipmentPayload/{equipmentCarrierId}', { equipmentCarrierId: id!, equipmentPartId });
        await reload();
    }

    async function savePayload(equipmentId: string) {
        await openApiFetchPost('/api/equipmentPayload', {}, { equipmentCarrierId: id!, equipmentPartId: equipmentId });
        await reload();
    }

    const showEquipmentSelect = () => {
        setIsEquipmentSelectVisible(true);
    };

    return (
        <>
            <TopBar equipmentId={props.params.id} activeTab="payload" />
            <Content>
                <Tray>
                    {(isLoading && <Spinner />) || (
                        <Panel>
                            <PanelBody>
                                <Table hover>
                                    <TableHead>
                                        <TableHeadRow light={false}>
                                            <TableHeader>{translate('common.name')}</TableHeader>
                                            <TableHeader>{translate('common.type')}</TableHeader>
                                            <TableHeader>{translate('common.status')}</TableHeader>
                                            <TableHeader align="right">{translate('equipment.expirationDate')}</TableHeader>
                                            <TableHeader>&nbsp;</TableHeader>
                                        </TableHeadRow>
                                    </TableHead>
                                    <TableBody>
                                        {data &&
                                            data.map((item) => (
                                                <TableRow key={item.id} onClick={() => navigate(`/equipment/${item.id}`)}>
                                                    <TableData> {item.name} </TableData>
                                                    <TableData> {item.equipmentType.name} </TableData>
                                                    <TableData> {translate(`equipment.${item.status}`)}</TableData>
                                                    <TableData alignRight>
                                                        {' '}
                                                        <EquipmentExpirationLabel data={item} />
                                                    </TableData>
                                                    <TableButtons>
                                                        <IconRemoveButton
                                                            removeQuestion={translate('common.confirmDelete', item.name)}
                                                            onClick={async () => removePayload(item.id)}
                                                        />
                                                    </TableButtons>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </PanelBody>
                            <EditPanelButtons footer>
                                <PanelButtonLink onClick={() => showEquipmentSelect()} primary text={translate('common.addItem', 'Payload')} />
                            </EditPanelButtons>
                        </Panel>
                    )}
                    {isEquipmentSelectVisible && <EquipmentSelect onClose={() => setIsEquipmentSelectVisible(false)} onSelected={savePayload} />}
                </Tray>
            </Content>
        </>
    );
}

function EquipmentSelect(props: { onClose: () => void; onSelected: (selectedEquipmentId: string) => void }) {
    const translate = useTranslate();
    const [equipmentTypes, setEquipmentTypes] = React.useState<ISelectOption[]>([]);
    const [equipment, setEquipment] = React.useState<ISelectOption[]>([]);
    const [equipmentTypeId, setEquipmentTypeId] = React.useState<undefined | string>();
    const [equipmentId, setEquipmentId] = React.useState<undefined | string>();

    React.useEffect(() => {
        (async () => {
            const responseEquipmentTypes = await openApiFetchGet('/api/equipmentTypes', { payload: 'true' });
            if (responseEquipmentTypes) {
                setEquipmentTypes(responseEquipmentTypes.map((item: { id: string; name: string }) => ({ text: item.name, value: item.id })));
            }
        })();
    }, []);

    React.useEffect(() => {
        (async () => {
            if (equipmentTypeId) {
                const responseEquipment = await openApiFetchGet('/api/equipment', {
                    equipmentTypeId,
                    availablePayload: 'true',
                    limit: '100',
                    offset: '0',
                    sortColumns: [{ columnName: 'name', direction: SortDirections.Ascending }],
                });
                if (responseEquipment) {
                    setEquipment(responseEquipment.items.map((item: { id: string; name: string }) => ({ text: item.name, value: item.id })));
                }
            }
        })();
    }, [equipmentTypeId]);

    const handleSaveClicked = () => {
        if (equipmentId) {
            props.onClose();
            props.onSelected(equipmentId);
        }
    };

    return (
        <Modal id="copy-dialog" onClose={() => props.onClose()}>
            <Modal.Title>
                <h3>
                    <span className="panel-title">{translate('common.addItem', 'Payload')}</span>
                </h3>
            </Modal.Title>
            <Modal.Content style={{ height: '200px' }}>
                <div className="admin-form" style={{ width: '100%' }}>
                    <EditPanel>
                        <EditPanelSection>
                            <EditPanelFilterSelect
                                name="equipmentTypeId"
                                value={equipmentTypeId}
                                handleChange={(value) => setEquipmentTypeId(value)}
                                topLabel={translate('equipment.types')}
                                placeholder={translate('common.selectItem', translate('equipment.types'))}
                                options={equipmentTypes}
                            />
                        </EditPanelSection>
                        <EditPanelSection>
                            <EditPanelFilterSelect
                                name="equipmentId"
                                value={equipmentId}
                                handleChange={(value) => setEquipmentId(value)}
                                topLabel={translate('equipment.equipment')}
                                placeholder={translate('common.selectItem', translate('equipment.equipment'))}
                                options={equipment}
                            />
                        </EditPanelSection>
                    </EditPanel>
                </div>
            </Modal.Content>
            <Modal.Actions>
                <EditPanelButtons fixWidth={true}>
                    <EditPanelButton small={true} text={translate('common.close')} onClick={() => props.onClose()} />
                    <EditPanelButton small={true} text={translate('common.save')} onClick={() => handleSaveClicked()} primary />
                </EditPanelButtons>
            </Modal.Actions>
        </Modal>
    );
}
